<template lang="pug">
  aiq-drawer(:model-value="true"
             direction="rtl"
             size="420px"
             :title="$t(`${I18N_PREFIX}.editor.title`)"
             :before-close="close")
    .connector-editor__body(v-loading="loading")
      template(v-if="form")
        .connector-editor__target
          .connector-editor__target-name {{ endpointName }}
          .connector-editor__target-call
            span.connector-editor__method {{ method }}
            span.connector-editor__path {{ path }}
          .connector-editor__target-host(v-if="domainLabel") {{ domainLabel }}

        .connector-editor__field
          label.connector-editor__label {{ $t(`${I18N_PREFIX}.editor.tool_name`) }}
          aiq-input(v-model="form.tool_name" size="small" :maxlength="MAX_TOOL_NAME_LENGTH")
          .connector-editor__hint {{ $t(`${I18N_PREFIX}.editor.tool_name_hint`) }}

        .connector-editor__field
          label.connector-editor__label {{ $t(`${I18N_PREFIX}.editor.purpose`) }}
          aiq-input(v-model="form.purpose"
                    type="textarea"
                    :rows="4"
                    size="small")
          .connector-editor__hint {{ $t(`${I18N_PREFIX}.editor.purpose_hint`) }}

        .connector-editor__field
          label.connector-editor__label {{ $t(`${I18N_PREFIX}.editor.sender_type`) }}
          aiq-select(v-model="form.sender_type" size="small" class="connector-editor__select")
            aiq-option(v-for="option in senderTypeOptions"
                       :key="option.value"
                       :label="option.label"
                       :value="option.value")
          .connector-editor__hint {{ $t(`${I18N_PREFIX}.editor.sender_type_hint`) }}

        .connector-editor__field.connector-editor__field--inline
          .connector-editor__switch-text
            label.connector-editor__label {{ $t(`${I18N_PREFIX}.editor.enabled`) }}
            .connector-editor__hint {{ $t(`${I18N_PREFIX}.editor.enabled_hint`) }}
          aiq-switch(v-model="form.enabled"
                     style="--aiq-switch-on-color: #30C28B; --aiq-switch-off-color: #C0CCDA")

        .connector-editor__field.connector-editor__field--inline
          .connector-editor__switch-text
            label.connector-editor__label {{ $t(`${I18N_PREFIX}.editor.allow_write`) }}
            .connector-editor__hint {{ $t(`${I18N_PREFIX}.editor.allow_write_hint`) }}
          aiq-switch(v-model="form.allow_write"
                     style="--aiq-switch-on-color: #30C28B; --aiq-switch-off-color: #C0CCDA")

        .connector-editor__warning(v-if="writeAccessMissing")
          | {{ $t(`${I18N_PREFIX}.write_access_required`) }}

        .connector-editor__actions
          aiq-button(type="danger" plain size="small" @click="remove")
            | {{ $t('global.delete') }}
          .connector-editor__actions-right
            aiq-button(size="small" @click="close") {{ $t('global.cancel') }}
            aiq-button(type="primary"
                       size="small"
                       :disabled="!canSave"
                       :loading="saving"
                       @click="save") {{ $t('global.save') }}
</template>

<script>
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import { mapState } from 'vuex';

import { EDITABLE_FIELDS, SENDER_TYPES, READ_ONLY_METHODS } from '@/store/modules/mcpConnectors';

const I18N_PREFIX = 'iq_tools_tab.connectors';

// Model providers accept ^[a-zA-Z0-9_-]{1,64}$ for a function name, and the
// backend rejects anything longer, so the input stops at the same limit.
const MAX_TOOL_NAME_LENGTH = 64;

export default {
  name: 'ConnectorEditor',
  props: {
    connectorId: {
      type: Number,
      required: true,
    },
  },
  emits: ['close'],
  data() {
    return {
      I18N_PREFIX,
      MAX_TOOL_NAME_LENGTH,
      loading: false,
      saving: false,
      form: null,
    };
  },
  computed: {
    ...mapState({
      connector: state => state.mcpConnectors.connectorItem,
    }),
    endpointName() {
      return get(this.connector, 'endpoint.name', this.$t(`${I18N_PREFIX}.editor.endpoint_missing`));
    },
    method() {
      return String(get(this.connector, 'endpoint.method', 'get')).toUpperCase();
    },
    path() {
      return get(this.connector, 'endpoint.path') || '/';
    },
    domainLabel() {
      const name = get(this.connector, 'domain.name');
      const host = get(this.connector, 'domain.host');
      return [name, host].filter(Boolean).join(' · ');
    },
    senderTypeOptions() {
      return SENDER_TYPES.map(value => ({
        value,
        label: this.$t(`${I18N_PREFIX}.sender_types.${value}`),
      }));
    },
    /*
     * An enabled write connector is not published as a tool until allow_write
     * is set, which the backend reports as a skip rather than an error. Saying
     * so here is the only place the person configuring it would find out.
     */
    writeAccessMissing() {
      const readOnly = READ_ONLY_METHODS.includes(this.method.toLowerCase());
      return Boolean(this.form) && this.form.enabled && !readOnly && !this.form.allow_write;
    },
    pristine() {
      return isEqual(this.form, pick(this.connector, EDITABLE_FIELDS));
    },
    canSave() {
      // purpose is what tells a model when to use the tool, so an empty one is
      // rejected by the backend on create and is no more useful on update.
      return !this.pristine
        && Boolean(get(this.form, 'tool_name', '').trim())
        && Boolean(get(this.form, 'purpose', '').trim());
    },
  },
  watch: {
    connectorId: {
      immediate: true,
      handler() {
        this.load();
      },
    },
  },
  methods: {
    /*
     * Read the connector back rather than taking the row from the list, so the
     * editor opens on what is stored.
     */
    async load() {
      this.loading = true;
      try {
        await this.$store.dispatch('mcpConnectors/getConnector', this.connectorId);
        this.form = pick(this.connector, EDITABLE_FIELDS);
      } catch (err) {
        this.notifyError(err, `${I18N_PREFIX}.errors.load_one`);
        this.$emit('close');
      } finally {
        this.loading = false;
      }
    },

    async save() {
      this.saving = true;
      try {
        await this.$store.dispatch('mcpConnectors/updateConnector', {
          id: this.connectorId,
          body: { ...this.form, tool_name: this.form.tool_name.trim() },
        });
        this.$aiq.notify.success(this.$t(`${I18N_PREFIX}.editor.saved`));
        this.$emit('close');
      } catch (err) {
        this.notifyError(err, `${I18N_PREFIX}.errors.save`);
      } finally {
        this.saving = false;
      }
    },

    async remove() {
      const confirmed = await this.$aiq.confirm(
        this.$t(`${I18N_PREFIX}.editor.delete_title`),
        this.$t(`${I18N_PREFIX}.editor.delete_content`),
      );
      if (!confirmed) {
        return;
      }

      try {
        await this.$store.dispatch('mcpConnectors/deleteConnector', this.connectorId);
        this.$aiq.notify.success(this.$t(`${I18N_PREFIX}.editor.deleted`));
        this.$emit('close');
      } catch (err) {
        this.notifyError(err, `${I18N_PREFIX}.errors.delete`);
      }
    },

    close() {
      this.$emit('close');
    },

    notifyError(err, fallbackKey) {
      this.$aiq.notify.error(get(err, 'response.data.message') || this.$t(fallbackKey));
    },
  },
};
</script>

<style lang="scss" scoped>
@import "./../../../styles/aiq-variables.scss";

.connector-editor__body {
  display: flex;
  flex-direction: column;
  gap: 18px;
  min-height: 120px;
  font-family: $aiq-font-family;
}

// The endpoint this connector points at, boxed like a section body.
.connector-editor__target {
  padding: 12px;
  background: #FFFFFF;
  border: 1px solid #E0E6ED;
}

.connector-editor__target-name {
  color: $aiq-text-main-column-list-item;
  font-size: 14px;
  font-weight: 500;
  line-height: 17px;
}

.connector-editor__target-call {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  color: $aiq-text-connection-status;
  font-size: 12px;
  font-weight: 300;
  line-height: 14px;
}

.connector-editor__target-host {
  margin-top: 4px;
  color: $aiq-text-connection-status;
  font-size: 12px;
  font-weight: 300;
  line-height: 14px;
}

.connector-editor__method {
  font-weight: 500;
  letter-spacing: 0.4px;
}

.connector-editor__path {
  word-break: break-all;
}

.connector-editor__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.connector-editor__field--inline {
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.connector-editor__switch-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.connector-editor__label {
  color: $aiq-text-subheader;
  font-size: 12px;
  font-weight: 500;
  line-height: 14px;
}

.connector-editor__hint {
  color: $aiq-text-connection-status;
  font-size: 12px;
  font-weight: 300;
  line-height: 14px;
}

.connector-editor__select {
  width: 100%;
}

.connector-editor__warning {
  padding: 10px 12px;
  background-color: $aiq-bgc-entity-orange;
  border: 1px solid #E0E6ED;
  color: $aiq-text-connection-title;
  font-size: 12px;
  font-weight: 300;
  line-height: 14px;
}

.connector-editor__actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 4px;
}

.connector-editor__actions-right {
  display: flex;
  gap: 8px;
}
</style>
